import { DataProviderFC, useInitialData } from '@notino/react-toolkit/renderer/fragment/useInitialData'
import { useCallback, useRef, useState } from 'react'
import axios from 'axios'

// types
import {
	BasePageData,
	BasePageQuery,
	BasePageRouteProps,
	ExtendedContextProps,
	PageContainerQuery,
	PageData,
	SetPageDataFn,
	SetPageDataFnReturnType,
	UsePageDataReturnType
} from '../types/types'

// utils
import { ERROR_PAGE_CODE } from '../utils/enums'

/**
 * Custom React hook for managing page data state, updates, and error handling in Notino page containers.
 *
 * This hook:
 * - Initializes page data from the server using useInitialData.
 * - Provides a setPageData function to update page data asynchronously
 * - Handles loading and fetching state.
 * - Implements error handling:
 *   - Handles Axios cancellation and network errors gracefully.
 *   - Maps HTTP error codes (404, 401, 403) to custom error codes for rendering appropriate error pages.
 *
 * @template TData       The shape of the page data.
 * @template TQuery      The shape of the query parameters.
 * @template TRouteProps The shape of additional route props.
 * @template InitialData The initial data type (defaults to PageData<TData>).
 *
 * @param container The page container component (used for SSR/SSG data hydration).
 * @param props     The props passed to the page container.
 * @returns         An object with { pageData, setPageData, isFetching }.
 */
const usePageData = <
	TData extends BasePageData,
	TQuery extends BasePageQuery,
	TRouteProps = BasePageRouteProps,
	InitialData extends PageData<TData> = PageData<TData>
>(
	container: DataProviderFC<ExtendedContextProps<PageContainerQuery<TQuery> & TRouteProps>, InitialData>,
	props: ExtendedContextProps<PageContainerQuery<TQuery> & TRouteProps>
): UsePageDataReturnType<TData> => {
	const initialData = useInitialData(container, props)

	const [pageData, setPageDataInternal] = useState<PageData<TData>>(initialData)
	const [isFetching, setIsFetching] = useState(false)

	// save data to ref to avoid using pageData as dependency in useCallback
	const pageDataRef = useRef<PageData<TData>>(pageData)

	const setPageData: SetPageDataFn<TData> = useCallback(async (updates, options) => {
		setIsFetching(true)

		let result: SetPageDataFnReturnType<TData> = { data: null, isCancelTokenError: false }

		try {
			const updateArray = Array.isArray(updates) ? updates : [updates]
			const results = await Promise.all(updateArray.map((u) => u.data))

			setPageDataInternal((prevState) => {
				if (!prevState || !prevState.success) {
					result = { ...result, data: prevState }
					return prevState
				}

				const newData: Partial<TData> = {}
				updateArray.forEach((update, idx) => {
					newData[update.dataKey] = results[idx]
				})

				const newState = { ...prevState, ...newData }
				result = { ...result, data: newState }

				if (options?.onSuccess) {
					const onSuccessPageData = options.onSuccess(prevState, newState)
					pageDataRef.current = onSuccessPageData
					return onSuccessPageData
				}
				pageDataRef.current = newState
				return newState
			})
			setIsFetching(false)
		} catch (error) {
			let data: PageData<TData> = {
				success: false,
				statusCodeToRender: ERROR_PAGE_CODE.GENERAL_ERROR
			}

			if (axios.isCancel(error)) {
				result = {
					...result,
					data: { ...data },
					isCancelTokenError: true
				}
			} else {
				if (axios.isAxiosError(error)) {
					/**
					 * If the error is a network error, we want to use the previous page data to avoid crashing the app.
					 */
					if (error.code === 'ERR_NETWORK') {
						data = pageDataRef.current
					} else {
						switch (error.response?.status) {
							/**
							 * The Notino layout service currently handles 401 and 403 errors as 404 pages.
							 * However, this logic is prepared to accommodate potential future changes.
							 */
							case 404:
								data = {
									...data,
									statusCodeToRender: ERROR_PAGE_CODE.NOT_FOUND
								}
								break
							case 401:
								data = {
									...data,
									statusCodeToRender: ERROR_PAGE_CODE.UNAUTHORIZED
								}
								break
							case 403:
								data = {
									...data,
									statusCodeToRender: ERROR_PAGE_CODE.FORBIDDEN
								}
								break
							default:
								break
						}
					}

					result = {
						...result,
						data
					}
				}

				setPageDataInternal(data)
				setIsFetching(false)
			}
		}

		return result
	}, [])

	return { pageData, setPageData, isFetching }
}

export default usePageData
